import { useEffect } from "react";
import { Link, NavLink } from "react-router-dom";

// Flip to "final" only after the owner approves the legal text.

export const C = {
  navy: "#071526", ocean: "#10263D", slate: "#19354D", cyan: "#55E4E7", teal: "#3DB9BE", white: "#F5F9FC", silver: "#A9B8C6",
};

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export const usePulseMeta = (title, description, path) => {
  useEffect(() => {
    document.title = title;
    setMeta("description", description);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", `https://veyronix.co.uk${path}`);
    window.scrollTo(0, 0);
  }, [title, description, path]);
};

export const PulseMark = ({ size = 36 }) => (
  <img src="/pulse-assets/pulse-icon-180.png" alt="PULSE logo" width={size} height={size} className="rounded-lg" style={{ width: size, height: size }} />
);

export const PulseLogo = () => (
  <Link to="/pulse" className="flex items-center gap-2.5" data-testid="pulse-logo">
    <PulseMark />
    <span className="leading-none">
      <span className="block font-heading font-bold tracking-[0.2em] text-lg" style={{ color: C.white }}>PULSE</span>
      <span className="block text-[10px] tracking-[0.25em] uppercase" style={{ color: C.silver }}>by Veyronix</span>
    </span>
  </Link>
);

const navCls = ({ isActive }) => `text-sm transition-colors ${isActive ? "text-[#55E4E7]" : "text-[#A9B8C6] hover:text-[#F5F9FC]"}`;

const Nav = () => (
  <header className="sticky top-0 z-40 border-b backdrop-blur-xl" style={{ borderColor: C.slate, background: "rgba(7,21,38,0.85)" }}>
    <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
      <PulseLogo />
      <nav className="flex items-center gap-4 sm:gap-6" data-testid="pulse-nav">
        <NavLink end to="/pulse" className={navCls} data-testid="pulse-nav-home">Home</NavLink>
        <NavLink to="/pulse/privacy-policy" className={navCls} data-testid="pulse-nav-privacy">Privacy</NavLink>
        <NavLink to="/pulse/terms" className={navCls} data-testid="pulse-nav-terms">Terms</NavLink>
      </nav>
    </div>
  </header>
);

const Footer = () => (
  <footer className="border-t mt-24" style={{ borderColor: C.slate, background: C.navy }} data-testid="pulse-footer">
    <div className="max-w-6xl mx-auto px-5 py-12 grid gap-8 md:grid-cols-3 text-sm" style={{ color: C.silver }}>
      <div className="space-y-3">
        <PulseLogo />
        <p>PULSE is a product of Veyronix Limited.</p>
      </div>
      <div className="space-y-1.5">
        <p className="font-semibold" style={{ color: C.white }}>Veyronix Limited</p>
        <p>Registered in England & Wales · Company no. 16821530</p>
        <p>64B Borthwick Road, London E15 1UE, United Kingdom</p>
      </div>
      <div className="space-y-1.5">
        <Link to="/pulse" className="block hover:text-white" data-testid="pulse-footer-home">PULSE home</Link>
        <Link to="/pulse/privacy-policy" className="block hover:text-white" data-testid="pulse-footer-privacy">Privacy Policy</Link>
        <Link to="/pulse/terms" className="block hover:text-white" data-testid="pulse-footer-terms">Terms of Service</Link>
        <Link to="/pulse/delete-account" className="block hover:text-white" data-testid="pulse-footer-delete-account">Deleting your account</Link>
        <a href="mailto:support@veyronix.co.uk" className="block hover:text-white" data-testid="pulse-footer-support">support@veyronix.co.uk</a>
        <a href="mailto:privacy@veyronix.co.uk" className="block hover:text-white" data-testid="pulse-footer-privacy-email">privacy@veyronix.co.uk</a>
        <a href="https://veyronix.co.uk" className="block hover:text-white" data-testid="pulse-footer-veyronix">veyronix.co.uk</a>
      </div>
    </div>
    <p className="text-center text-xs pb-8" style={{ color: C.silver }}>© {new Date().getFullYear()} Veyronix Limited. All rights reserved.</p>
  </footer>
);

export const PulseLayout = ({ children }) => (
  <div className="min-h-screen font-body antialiased selection:bg-[#55E4E7] selection:text-[#071526]" style={{ background: C.navy, color: C.white }} data-testid="pulse-layout">
    <Nav />
    <main>{children}</main>
    <Footer />
  </div>
);
